import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

// Always relative — nginx proxies /api to the API on the same origin
const TELEMETRY_URL = "/api/telemetry/logs";
const APP_LABEL = import.meta.env.VITE_LOKI_APP_LABEL ?? "yalana-webapp";
// Enabled in production builds by default; opt-out via VITE_TELEMETRY_ENABLED=false
const ENABLED =
  import.meta.env.VITE_TELEMETRY_ENABLED !== "false" && import.meta.env.PROD;

interface TelemetryEntry {
  level: string;
  message: string;
  timestamp: number;
  [key: string]: unknown;
}

const buffer: TelemetryEntry[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush(): void {
  if (flushTimeout !== null) return;
  flushTimeout = setTimeout(flush, 5_000);
}

function flush(): void {
  flushTimeout = null;
  if (buffer.length === 0) return;
  const entries = buffer.splice(0);
  navigator.sendBeacon(
    TELEMETRY_URL,
    JSON.stringify({ app: APP_LABEL, entries }),
  );
}

function push(
  entry: Omit<TelemetryEntry, "timestamp"> & Record<string, unknown>,
): void {
  if (!ENABLED) return;
  buffer.push({ ...entry, timestamp: Date.now() } as TelemetryEntry);
  scheduleFlush();
}

function captureErrors(): void {
  window.addEventListener("error", (e) => {
    push({
      level: "error",
      message: e.message,
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    push({
      level: "error",
      message: String(e.reason?.message ?? e.reason ?? "Unhandled rejection"),
    });
  });
}

function captureWebVitals(): void {
  const report =
    (name: string) => (metric: { value: number; rating: string }) => {
      push({
        level: "info",
        message: `web-vital:${name}`,
        value: metric.value,
        rating: metric.rating,
      });
    };
  onCLS(report("CLS"));
  onFCP(report("FCP"));
  onINP(report("INP"));
  onLCP(report("LCP"));
  onTTFB(report("TTFB"));
}

export function initTelemetry(): void {
  if (!ENABLED) return;
  captureErrors();
  captureWebVitals();
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
